<template>
  <Form
    v-slot="$form"
    :resolver="resolver"
    @submit="onFormSubmit"
    class="flex flex-col gap-4"
  >
    <div>
      <label
        for="email"
        class="block text-surface-900 dark:text-surface-0 text-xl font-medium mb-2"
        >Email</label
      >
      <InputText
        id="email"
        name="email"
        type="text"
        placeholder="Adresse mail"
        class="w-full"
        v-model="email"
      />
      <Message
        v-if="$form.email?.invalid"
        severity="error"
        size="small"
        variant="simple"
        >{{ $form.email.error?.message }}</Message
      >
    </div>
    <div>
      <label
        for="password"
        class="block text-surface-900 dark:text-surface-0 font-medium text-xl"
        >Mot de passe</label
      >
      <Password
        id="password"
        name="password"
        v-model="password"
        placeholder="Mot de passe"
        :toggleMask="true"
        class="mb-4"
        fluid
        :feedback="false"
      ></Password>
      <Message
        v-if="$form.password?.invalid"
        severity="error"
        size="small"
        variant="simple"
        >{{ $form.password.error?.message }}</Message
      >
    </div>
    <Message v-if="erreur" severity="error" role="alert">{{ erreur }}</Message>
    <Button
      :loading="loading"
      :disabled="!$form.valid"
      label="Se connecter"
      class="w-full"
      type="submit"
    ></Button>
  </Form>
</template>
<script setup lang="ts">
import Password from "primevue/password";
import { Form } from "@primevue/forms";
import Message from "primevue/message";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import { ref, defineEmits } from "vue";
import { yupResolver } from "@primevue/forms/resolvers/yup";
import seConnecterYup from "@/yup/se-connecter";
import store from "@/store";
import jwt_decode from "jwt-decode";

const emit = defineEmits(["success"]);

const email = ref("");
const password = ref("");

const resolver = ref(yupResolver(seConnecterYup));
const loading = ref(false);
const erreur = ref("");

const IDENTIFIANTS_INCORRECTS = "Adresse mail ou mot de passe incorrect.";
const CONNEXION_IMPOSSIBLE =
  "Connexion impossible pour le moment. Réessayez dans un instant.";

// Message a afficher pour une reponse en echec de /login. L'API repond 401
// avec « Invalid credentials. » pour un mauvais couple email / mot de passe, et
// avec un message en francais pour un compte refuse (« Compte inactif. »,
// « Compte désactivé depuis la date de sortie. ») : celui-la est repris tel quel.
const messageEchec = async (res: Response) => {
  if (res.status !== 401) {
    return CONNEXION_IMPOSSIBLE;
  }

  const corps = await res.json().catch(() => null);
  const message = corps?.message;

  return message && message !== "Invalid credentials."
    ? message
    : IDENTIFIANTS_INCORRECTS;
};

const onFormSubmit = ({ valid, values }) => {
  if (!valid) {
    return;
  }

  loading.value = true;
  erreur.value = "";

  fetch(process.env.VUE_APP_API_URL + "/login", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(values),
  })
    .then(async (res) => {
      if (!res.ok) {
        erreur.value = await messageEchec(res);
        return;
      }

      const response = await res.json();
      const accessToken = response.access_token ?? response.token;
      const user = jwt_decode(accessToken) as {
        exp?: number;
        societe?: string;
      };

      // L'API peut ne pas renvoyer expires_in : on le déduit de la
      // claim `exp` du JWT pour conserver une expiration de session valide.
      const expiresIn =
        response.expires_in ??
        (user.exp ? user.exp - Math.floor(Date.now() / 1000) : 0);

      store.commit("setAuth", {
        access_token: accessToken,
        expires_in: expiresIn,
        user,
      });

      // À défaut de sélection persistée, on bascule sur la société de l'utilisateur.
      if (!localStorage.getItem("selected_societe")) {
        store.commit("domain/setSelectedSociete", user?.societe);
      }

      emit("success");
    })
    .catch((error) => {
      console.error("login error", error);
      erreur.value = CONNEXION_IMPOSSIBLE;
    })
    .then(() => {
      loading.value = false;
    });
};
</script>
